import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImages, faVideo, faFileAlt, faInfoCircle, faUserCircle, faEnvelope, faGlobe, faRobot, faDatabase, faMoneyBill, faGift, faGamepad, faBullhorn, faTools, faThumbsUp, faDollarSign, faSyncAlt, faCloudUploadAlt, faUsers, faRocket, faChartLine, faShieldAlt, faClock } from '@fortawesome/free-solid-svg-icons';
import Header from '../../component/layout/header';
import Footer from '../../component/layout/footer';
import Navigate from '../../../../util/Navigate';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import IndieSubmenu from '../../component/section/publishers/IndieSubmenu';

function PublishersMediaKitPage() {
    const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

    return (
        <>
            <Helmet prioritizeSeoTags>
                <meta charSet="utf-8" />
                <title>Create a Professional Media Kit for Your Game | Glitch Gaming</title>
                <meta name="description" content="Use Glitch's free media kit builder to create a comprehensive media kit for your game. Include logos, screenshots, trailers, press releases, and more." />
                <meta name="robots" content="index, follow" />
                <meta property="og:title" content="Create a Professional Media Kit for Your Game | Glitch" />
                <meta property="og:description" content="Use Glitch's free media kit builder to create a comprehensive media kit for your game. Include logos, screenshots, trailers, press releases, and more." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={currentUrl} />
                <meta property="og:image" content="https://www.glitch.fun/assets/images/publishers/media_kit_banner.webp" />
            </Helmet>
            <Header />
            
            {/* Hero Section */}
            <section className="media-kit-hero">
                <div className="container">
                    <div className="row align-items-center">
                        <div className="col-lg-6 text-center text-lg-start">
                            <h1 className="display-4 fw-bold mb-4 pt-5">
                                Build Your Game's <span className="text-gradient">Professional Media Kit</span>
                            </h1>
                            <p className="lead mb-5">Create an impressive press package that journalists and influencers love. Showcase your game in the best light with our easy-to-use builder.</p>
                            <div className="cta-group">
                                <Link to={Navigate.publishersOnboardingStep1Page()} className="btn btn-primary btn-lg me-3 mb-3">
                                    Start Building Free <FontAwesomeIcon icon={faRocket} className="ms-2" />
                                </Link>
                                <Link to={Navigate.demoPage()} className="btn btn-outline-light btn-lg mb-3">
                                    Book A Demo
                                </Link>
                            </div>
                        </div>
                        <div className="col-lg-6 pt-5">
                            <div className="hero-visual mt-5 mt-lg-0">
                                <img
                                    src="/assets/images/publishers/media_kit_builder.png"
                                    alt="Media Kit Builder Preview"
                                    className="img-fluid rounded-3 shadow-lg border border-3 border-white"
                                />
                                <div className="highlight-card">
                                    <FontAwesomeIcon icon={faFileAlt} className="me-2" />
                                    Professional Templates
                                </div>
                                <div className="highlight-card">
                                    <FontAwesomeIcon icon={faCloudUploadAlt} className="me-2" />
                                    Easy Asset Management
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Value Propositions */}
            <section className="value-propositions py-5 bg-light">
                <div className="container text-black">
                    <h2 className="text-center mb-5 text-black">Everything You Need for Press Success</h2>
                    <div className="row g-4">
                        <div className="col-md-4">
                            <div className="value-card h-100">
                                <div className="icon-wrapper bg-primary">
                                    <FontAwesomeIcon icon={faImages} className="fa-2x" />
                                </div>
                                <h3 className='text-black'>Visual Assets</h3>
                                <ul className="value-list">
                                    <li>High-res logos & icons</li>
                                    <li>Screenshot galleries</li>
                                    <li>Trailer integration</li>
                                    <li>Brand guidelines</li>
                                </ul>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="value-card h-100">
                                <div className="icon-wrapper bg-success">
                                    <FontAwesomeIcon icon={faFileAlt} className="fa-2x" />
                                </div>
                                <h3 className='text-black'>Press Materials</h3>
                                <ul className="value-list">
                                    <li>Press release templates</li>
                                    <li>Fact sheets</li>
                                    <li>Developer bios</li>
                                    <li>FAQ sections</li>
                                </ul>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="value-card h-100">
                                <div className="icon-wrapper bg-danger">
                                    <FontAwesomeIcon icon={faChartLine} className="fa-2x" />
                                </div>
                                <h3 className='text-black'>Analytics</h3>
                                <ul className="value-list">
                                    <li>Download tracking</li>
                                    <li>Press contact management</li>
                                    <li>Asset performance reports</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Feature Section */}
            <section className="media-kit-features py-5">
                <div className="container text-black">
                    <div className="row g-5 align-items-center">
                        <div className="col-lg-6">
                            <img 
                                src="/assets/images/publishers/media_kit_example.jpg" 
                                alt="Media Kit Example" 
                                className="img-fluid rounded-3 shadow-lg border border-white border-2"
                            />
                        </div>
                        <div className="col-lg-6">
                            <h2 className="mb-4 text-black">Why Glitch Media Kits Convert Better</h2>
                            <div className="feature-list">
                                <div className="feature-item">
                                    <div className="feature-icon bg-primary">
                                        <FontAwesomeIcon icon={faThumbsUp} />
                                    </div>
                                    <div className="feature-text">
                                        <h4>Professional First Impressions</h4>
                                        <p>Curated templates that match industry standards and journalist expectations</p>
                                    </div>
                                </div>
                                <div className="feature-item">
                                    <div className="feature-icon bg-success">
                                        <FontAwesomeIcon icon={faSyncAlt} />
                                    </div>
                                    <div className="feature-text">
                                        <h4>Automatic Updates</h4>
                                        <p>Keep all press materials synchronized with your latest game updates</p>
                                    </div>
                                </div>
                                <div className="feature-item">
                                    <div className="feature-icon bg-danger">
                                        <FontAwesomeIcon icon={faUsers} />
                                    </div>
                                    <div className="feature-text">
                                        <h4>Collaboration Tools</h4>
                                        <p>Invite team members and manage access levels</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="media-kit-cta py-5 bg-gradient">
                <div className="container text-center">
                    <h2 className="text-white mb-4">Start Building Your Media Kit Now</h2>
                    <p className="lead text-white mb-5">Join 1,500+ developers who increased their press coverage by an average of 3x</p>
                    <div className="d-flex justify-content-center gap-3">
                        <Link 
                            to={Navigate.publishersOnboardingStep1Page()} 
                            className="btn btn-light btn-lg px-5 py-3 fw-bold"
                        >
                            Create Free Media Kit
                        </Link>
                    </div>
                    <div className="trust-badges mt-5">
                        <div className="badge-item">
                            <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
                            Secure Cloud Storage
                        </div>
                        <div className="badge-item">
                            <FontAwesomeIcon icon={faClock} className="me-2" />
                            24/7 Access
                        </div>
                    </div>
                </div>
            </section>

            <IndieSubmenu />
            <Footer />

            <style jsx>{`
                .media-kit-hero {
                    padding: 100px 0;
                    background: linear-gradient(135deg, #1a1b2f 0%, #080e37 100%);
                    color: white;
                }

                .text-gradient {
                    background: linear-gradient(90deg, #ff0052 0%, #9400ff 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                }

                .hero-visual {
                    position: relative;
                    border-radius: 20px;
                    overflow: hidden;
                    transform: perspective(1000px) rotateY(-5deg);
                    box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25);
                }

                .highlight-card {
                    position: absolute;
                    padding: 1rem 1.5rem;
                    border-radius: 12px;
                    backdrop-filter: blur(10px);
                    background: rgba(255,255,255,0.1);
                    border: 1px solid rgba(255,255,255,0.2);
                    max-width: 250px;
                    animation: float 6s ease-in-out infinite;
                }

                @keyframes float {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-20px); }
                }

                .value-propositions {
                    background: #f8f9ff;
                }

                .value-card {
                    background: white;
                    padding: 2rem;
                    border-radius: 16px;
                    box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1);
                    transition: transform 0.3s ease;
                }

                .value-card:hover {
                    transform: translateY(-10px);
                }

                .icon-wrapper {
                    width: 60px;
                    height: 60px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin-bottom: 1.5rem;
                }

                .media-kit-features {
                    background: linear-gradient(180deg, #ffffff 0%, #f8f9ff 100%);
                }

                .feature-item {
                    display: flex;
                    align-items: center;
                    margin-bottom: 2rem;
                    padding: 1.5rem;
                    border-radius: 12px;
                    background: white;
                    box-shadow: 0 5px 15px rgba(0,0,0,0.05);
                    transition: transform 0.3s ease;
                }

                .feature-item:hover {
                    transform: translateX(10px);
                }

                .feature-icon {
                    width: 50px;
                    height: 50px;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.2rem;
                    color: white;
                    margin-right: 1.5rem;
                    flex-shrink: 0;
                }

                .media-kit-cta {
                    background: linear-gradient(135deg, #ff0052 0%, #9400ff 100%);
                }

                .trust-badges {
                    display: flex;
                    justify-content: center;
                    gap: 2rem;
                    margin-top: 2rem;
                }

                .badge-item {
                    display: flex;
                    align-items: center;
                    padding: 0.5rem 1rem;
                    background: rgba(255,255,255,0.1);
                    border-radius: 50px;
                    border: 1px solid rgba(255,255,255,0.2);
                }

                @media (max-width: 768px) {
                    .media-kit-hero {
                        padding: 60px 0;
                    }
                    
                    .hero-visual {
                        transform: none;
                    }
                    
                    .feature-item {
                        flex-direction: column;
                        text-align: center;
                    }
                    
                    .feature-icon {
                        margin-right: 0;
                        margin-bottom: 1rem;
                    }
                }
            `}</style>
        </>
    );
}

export default PublishersMediaKitPage;